import { useEffect, useRef } from 'react';
import { env } from '@config/env';
import { initHotjar } from '@core/analytics/hotjar';
import { markReload } from '@core/reloadReason';
import { useConsentStore } from './consentStore';
import { analyticsAllowed } from './consentPolicy';
import { useConsentBootstrap } from './useConsentBootstrap';
import { useConsentSync } from './useConsentSync';
import { useTermsAcceptanceSync } from './useTermsAcceptanceSync';
import { ConsentBanner } from './components/ConsentBanner';
import { ConsentPreferencesDialog } from './components/ConsentPreferencesDialog';

/**
 * App-root privacy layer — mounted once (in `App`, OUTSIDE the AuthGuard) so it covers pre-auth pages too
 * (an EU visitor on the login screen must not be tracked before consenting). It:
 *  - runs the non-blocking geo/consent bootstrap (`useConsentBootstrap`),
 *  - gates Hotjar on the consent decision (`HotjarGate`),
 *  - (F3) will also render the `ConsentBanner`.
 * Renders nothing visible in F2.
 */
export function PrivacyLayer() {
  useConsentBootstrap();
  useConsentSync();
  useTermsAcceptanceSync();
  const prefsOpen = useConsentStore((s) => s.prefsOpen);
  const closePreferences = useConsentStore((s) => s.closePreferences);
  return (
    <>
      <HotjarGate />
      <ConsentBanner />
      {/* Mounted once here so the banner, Cookie Policy, and Settings can all reopen it. */}
      <ConsentPreferencesDialog open={prefsOpen} onClose={closePreferences} />
    </>
  );
}

/**
 * Loads Hotjar ONLY when analytics is allowed (out-of-scope, or in-scope + granted), and only if a site id
 * is configured (`VITE_HOTJAR_ID` — empty locally ⇒ never loads). Deferred: while the async geo check is
 * `pending`, analytics is "held", so Hotjar waits for the result instead of loading eagerly for EU visitors.
 *
 * Withdrawal: Hotjar has no clean client-side unload once its tag is injected, so if consent is withdrawn
 * after the tag loaded we reload the page — the reloaded session sees `analytics:false` and never re-injects
 * it. (Rare action; acceptable to reload there.)
 */
function HotjarGate() {
  const scope = useConsentStore((s) => s.scope);
  const consent = useConsentStore((s) => s.consent);
  const allowed = analyticsAllowed(scope, consent);
  const loadedRef = useRef(false);

  useEffect(() => {
    if (!env.hotjarId) return; // no site id (e.g. local dev) → nothing to gate
    if (allowed && !loadedRef.current) {
      loadedRef.current = true;
      initHotjar(env.hotjarId);
    } else if (!allowed && loadedRef.current) {
      // Consent withdrawn after the tag was injected — no unload API; drop it via a reload.
      markReload('consent', 'analytics consent withdrawn');
      window.location.reload();
    }
  }, [allowed]);

  return null;
}
